<template>
  <ul class="ml-2 grow list-inside list-disc text-base text-type-secondary">
    <template v-if="isEarthquake">
      <li>Time recorded: {{ reportedAtFormatted }}</li>
      <li>Last updated: {{ lastUpdatedFormatted }}</li>
      <li>Recorded near: {{ selectedIncident?.reportedNear }}</li>
      <li>Magnitude: {{ roundedByOneDecimal(selectedIncident?.earthquakeMagnitude) }}</li>
      <li>Depth: {{ roundedByOneDecimal(selectedIncident?.earthquakeDepth) }} km</li>
      <li>Issued by: Geoscience Australia</li>
    </template>
    <template v-else>
      <li v-if="reportedAtFormatted">Time reported: {{ reportedAtFormatted }}</li>
      <li>Last updated: <ui-relative-time :time="selectedIncident?.updatedAt" /></li>
      <li>Response: {{ selectedIncident?.incidentStatus }}</li>
      <li v-if="selectedIncident?.reportedNear">Reported near: {{ selectedIncident?.reportedNear }}</li>
      <li v-else-if="selectedIncident?.reportedIn">Reported in: {{ selectedIncident?.reportedIn }}</li>
    </template>
  </ul>
  <div
    v-if="isEarthquake"
    class="text-base font-semibold [&_a]:text-indigo-500 [&_a]:underline"
    v-html="submitFeltReportNoteWithTarget"
  ></div>
</template>

<script setup lang="ts">
import { getDatetimeOrdinalSuffix } from '@/common/helpers';
import { useHazardsStore, useMapStore } from '@/features/hazards';
import { DateTime } from 'luxon';
import { computed } from 'vue';

const map = useMapStore();
const hazards = useHazardsStore();

const isEarthquake = computed(() => {
  return selectedIncident?.value?.entitySubType === 'incidents_earthquake';
});

const selectedIncident = computed(() => {
  return hazards?.hazards?.incidents.find((incident) =>
    [hazards.selectedHazard?.hazardId, map.selectedPopupHazard?.hazardId].includes(incident.id)
  );
});

const reportedAtFormatted = computed(() => {
  return formatIncidentDateTime(selectedIncident?.value?.reportedAt);
});

const lastUpdatedFormatted = computed(() => {
  return formatIncidentDateTime(selectedIncident?.value?.updatedAt);
});

const submitFeltReportNoteWithTarget = computed(() => selectedIncident.value?.submitFeltReportNote?.replace(/<a /g, '<a target="_blank" '));

const formatIncidentDateTime = (dateTime: string | undefined) => {
  if (!dateTime) {
    return '';
  }

  const timestamp = DateTime.fromISO(dateTime);
  // time in am/pm format
  const format1 = timestamp.toFormat('h:mm');
  const format2 = timestamp.toFormat('a');

  // day
  const format3 = timestamp.toFormat('EEEE d');
  const format4 = getDatetimeOrdinalSuffix(timestamp.day);

  // month
  const format5 = timestamp.toFormat('MMMM');

  const formattedDateTime = `${format1} ${format2}, ${format3}${format4} ${format5}`;

  return formattedDateTime;
};

const roundedByOneDecimal = (value: string | undefined) => {
  if (value === undefined) {
    return '';
  }
  return Math.round(parseFloat(value) * 10) / 10;
};
</script>
